import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Alert,
  Snackbar,
  Skeleton,
  Tooltip,
  Chip,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Language,
  Palette,
  ContentCopy,
  OpenInNew,
  CheckCircle,
  Info,
  Block,
} from '@mui/icons-material';
import api from '../../utils/api';
import { THEMES } from '../../components/landing-themes';
import DesktopNav from '../../components/DesktopNav';
import LibrarianBottomNav from '../../components/LibrarianBottomNav';

/**
 * Website Settings Page for Librarians
 * Allows customization of landing page theme, accent color, and contact info
 */
function WebsiteSettings() {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form state
  const [formData, setFormData] = useState({
    website_theme: 'modern',
    website_accent_color: '#F26522',
    contact_email: '',
    contact_phone: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/librarian/website-settings');
      if (!response.data.error) {
        const data = response.data.data;
        setSettings(data);
        setFormData({
          website_theme: data.website_theme || 'modern',
          website_accent_color: data.website_accent_color || '#F26522',
          contact_email: data.contact_email || '',
          contact_phone: data.contact_phone || '',
        });
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      showSnackbar('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await api.put('/librarian/website-settings', formData);
      if (!response.data.error) {
        setSettings(response.data.data);
        showSnackbar('Settings saved successfully', 'success');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      showSnackbar(err.response?.data?.message || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar('URL copied to clipboard', 'success');
  };

  const websiteUrl = settings?.website_slug
    ? `https://bookmylibrary.in/l/${settings.website_slug}`
    : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      <DesktopNav />
      <Box sx={{ minHeight: 'calc(100vh - 64px)', pb: '110px' }}>
        <Container maxWidth="lg" sx={{ py: 3 }}>
          {/* Header */}
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <IconButton
              onClick={() => navigate('/librarian/manage')}
              sx={{
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' }
              }}
            >
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Website Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Customize your library's landing page
              </Typography>
            </Box>
          </Box>

          {loading ? (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Skeleton variant="rounded" height={200} />
              </Grid>
              <Grid item xs={12}>
                <Skeleton variant="rounded" height={300} />
              </Grid>
            </Grid>
          ) : !settings?.website_enabled ? (
            // Website not enabled message
            <Card sx={{ textAlign: 'center', py: 6 }}>
              <CardContent>
                <Block sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Website Not Enabled
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                  Your library's landing page has not been enabled by the administrator.
                  Please contact the admin to enable this feature.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {/* Website URL Card */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Language color="primary" />
                      <Typography variant="h6" fontWeight={600}>
                        Your Website URL
                      </Typography>
                      <Chip label="Live" color="success" size="small" icon={<CheckCircle />} />
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily: 'monospace',
                          flex: 1,
                          minWidth: 200,
                          wordBreak: 'break-all',
                        }}
                      >
                        {websiteUrl}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Copy URL">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ContentCopy />}
                            onClick={() => copyToClipboard(websiteUrl)}
                          >
                            Copy
                          </Button>
                        </Tooltip>
                        <Tooltip title="Open in new tab">
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<OpenInNew />}
                            onClick={() => window.open(websiteUrl, '_blank')}
                          >
                            Preview
                          </Button>
                        </Tooltip>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Theme Selection */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <Palette color="primary" />
                      <Typography variant="h6" fontWeight={600}>
                        Choose Theme
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      {Object.values(THEMES).map((theme) => (
                        <Grid item xs={6} sm={4} md={2.4} key={theme.id}>
                          <Card
                            onClick={() => setFormData({ ...formData, website_theme: theme.id })}
                            sx={{
                              cursor: 'pointer',
                              border: '2px solid',
                              borderColor: formData.website_theme === theme.id ? 'primary.main' : 'grey.200',
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: 'primary.main',
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            <CardContent sx={{ textAlign: 'center', p: 2 }}>
                              <Box
                                sx={{
                                  height: 60,
                                  mb: 1,
                                  borderRadius: 1,
                                  bgcolor: theme.swatchBg || '#fafafa',
                                  border: '1px solid #eee',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.swatchFg || '#333',
                                    fontWeight: 600,
                                  }}
                                >
                                  {theme.name[0]}
                                </Typography>
                              </Box>
                              <Typography variant="body2" fontWeight={500}>
                                {theme.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {theme.description}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Accent Color */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <Palette color="primary" />
                      <Typography variant="h6" fontWeight={600}>
                        Accent Color
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <input
                        type="color"
                        value={formData.website_accent_color}
                        onChange={(e) => setFormData({ ...formData, website_accent_color: e.target.value })}
                        style={{
                          width: 60,
                          height: 60,
                          padding: 0,
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={formData.website_accent_color}
                        onChange={(e) => setFormData({ ...formData, website_accent_color: e.target.value })}
                        placeholder="#F26522"
                        sx={{ width: 120 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {['#F26522', '#2196F3', '#4CAF50', '#9C27B0', '#FF5722', '#00BCD4'].map((color) => (
                          <Box
                            key={color}
                            onClick={() => setFormData({ ...formData, website_accent_color: color })}
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: color,
                              cursor: 'pointer',
                              border: formData.website_accent_color === color ? '3px solid #333' : '2px solid #eee',
                              transition: 'transform 0.2s',
                              '&:hover': { transform: 'scale(1.1)' },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    <Alert severity="info" sx={{ mt: 2 }} icon={<Info />}>
                      This color will be used for buttons, highlights, and accents on your landing page.
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>

              {/* Contact Information */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Contact Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      This will be displayed on your landing page. Leave blank to use your account details.
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Contact Email"
                          type="email"
                          value={formData.contact_email}
                          onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                          placeholder="library@example.com"
                          helperText={!formData.contact_email ? `Will use: ${settings?.librarian_email || 'your account email'}` : ''}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Contact Phone"
                          value={formData.contact_phone}
                          onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                          placeholder="+91 98765 43210"
                          helperText={!formData.contact_phone ? `Will use: ${settings?.librarian_phone || 'your account phone'}` : ''}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Save Button */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => window.open(websiteUrl, '_blank')}
                    startIcon={<OpenInNew />}
                  >
                    Preview Website
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                      minWidth: 150,
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <LibrarianBottomNav />
    </Box>
  );
}

export default WebsiteSettings;
